"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useLinkTypes, useCreateLinkType } from "@/hooks/use-link-types"
import { toast } from "sonner"
import { Plus, Check, X, Loader2 } from "lucide-react"

interface TypeSelectProps {
  value: string
  onValueChange: (value: string) => void
  activeOnly?: boolean
  placeholder?: string
}

export function TypeSelect({
  value,
  onValueChange,
  activeOnly = true,
  placeholder = "Select type...",
}: TypeSelectProps) {
  const { data: linkTypes = [] } = useLinkTypes()
  const { mutate: createType, isPending } = useCreateLinkType()
  const [adding, setAdding] = useState(false)
  const [newNamespace, setNewNamespace] = useState("")
  const [newLabel, setNewLabel] = useState("")

  const types = activeOnly
    ? linkTypes.filter((lt) => lt.is_active)
    : linkTypes

  const handleAdd = () => {
    if (!newNamespace.trim() || !newLabel.trim()) {
      toast.error("Namespace and label are required")
      return
    }
    createType(
      {
        namespace: newNamespace.trim().toLowerCase(),
        label: newLabel.trim(),
      },
      {
        onSuccess: () => {
          const ns = newNamespace.trim().toLowerCase()
          toast.success(`Created type "${ns}"`)
          onValueChange(ns)
          setNewNamespace("")
          setNewLabel("")
          setAdding(false)
        },
        onError: (err) => toast.error(err.message),
      },
    )
  }

  if (adding) {
    return (
      <div className="flex items-center gap-2">
        <Input
          value={newNamespace}
          onChange={(e) => setNewNamespace(e.target.value)}
          placeholder="ns"
          className="h-9 w-16"
          autoFocus
        />
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Label"
          className="h-9 flex-1"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              handleAdd()
            }
            if (e.key === "Escape") setAdding(false)
          }}
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-8"
          onClick={handleAdd}
          disabled={isPending}
        >
          {isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Check className="size-3.5 text-green-500" />
          )}
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-8"
          onClick={() => setAdding(false)}
        >
          <X className="size-3.5 text-red-500" />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="flex-1">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {types.map((lt) => (
            <SelectItem key={lt.namespace} value={lt.namespace}>
              {lt.label} ({lt.namespace})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        size="icon"
        variant="outline"
        className="size-9 shrink-0"
        onClick={() => setAdding(true)}
        title="Add new type"
      >
        <Plus className="size-4" />
      </Button>
    </div>
  )
}
